import { useState, useRef, useEffect } from 'react';

interface HeaderProps {
  onWhatsAppClick: () => void;
  onPlayRadio?: () => void;
  isPlaying?: boolean;
}

interface SubMenuItem {
  label: string;
  sectionId: string;
  icon: string;
}

interface NavMenuItem {
  label: string;
  icon: string;
  items: SubMenuItem[];
}

const navMenu: NavMenuItem[] = [
  {
    label: 'DJ Jean',
    icon: 'ri-user-star-line',
    items: [
      { label: 'Sobre', sectionId: 'sobre', icon: 'ri-information-line' },
      { label: 'Depoimentos', sectionId: 'depoimentos', icon: 'ri-chat-quote-line' },
    ],
  },
  {
    label: 'Serviços',
    icon: 'ri-service-line',
    items: [
      { label: 'Eventos & Festas', sectionId: 'servicos', icon: 'ri-star-line' },
      { label: 'Área de Cobertura', sectionId: 'cobertura', icon: 'ri-map-pin-line' },
    ],
  },
  {
    label: 'Mídia',
    icon: 'ri-play-circle-line',
    items: [
      { label: 'Rádio Flashback', sectionId: 'radio', icon: 'ri-radio-line' },
      { label: 'TV Ao Vivo', sectionId: 'tv', icon: 'ri-tv-2-line' },
      { label: 'Galeria de Fotos', sectionId: 'galeria', icon: 'ri-image-line' },
    ],
  },
  {
    label: 'Loja',
    icon: 'ri-store-2-line',
    items: [
      { label: 'Pendrive Flashback', sectionId: 'pendrive', icon: 'ri-usb-line' },
    ],
  },
  {
    label: 'Contato',
    icon: 'ri-phone-line',
    items: [
      { label: 'Fale Conosco', sectionId: 'contato', icon: 'ri-message-3-line' },
      { label: 'Alegria & Festa', sectionId: 'festa', icon: 'ri-emotion-happy-line' },
    ],
  },
];

export default function Header({ onWhatsAppClick, onPlayRadio, isPlaying }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeMobileMenu, setActiveMobileMenu] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setActiveMobileMenu(null);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-black/97 backdrop-blur-sm sticky top-0 z-50 border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14">

          {/* Logo */}
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-red-500 to-orange-500 rounded-full">
              <i className="ri-music-2-fill text-white text-sm"></i>
            </div>
            <h1 className="text-white text-base font-bold tracking-wide">DJ Jean</h1>
          </div>

          {/* Player Rádio - Desktop */}
          {onPlayRadio && (
            <div className="hidden lg:flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-full px-4 py-1.5 border border-white/10">
              <div className="flex items-center space-x-1.5">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-white/70 text-xs font-medium">AO VIVO</span>
              </div>
              <button
                onClick={onPlayRadio}
                className="flex items-center space-x-1.5 text-white hover:text-orange-400 transition-colors cursor-pointer"
              >
                <i className={`${isPlaying ? 'ri-pause-circle-fill' : 'ri-play-circle-fill'} text-base`}></i>
                <span className="text-xs font-medium whitespace-nowrap">
                  {isPlaying ? 'Pausar' : 'Tocar'} Rádio
                </span>
              </button>
              <div className="flex items-end space-x-0.5 h-4">
                {[3, 4, 2, 5, 3].map((h, i) => (
                  <div
                    key={i}
                    className={`w-0.5 bg-orange-400 rounded-full transition-all ${isPlaying ? 'animate-pulse' : ''}`}
                    style={{ height: `${h * 2}px`, animationDelay: `${i * 100}ms` }}
                  ></div>
                ))}
              </div>
            </div>
          )}

          {/* Nav Desktop com Dropdowns */}
          <nav ref={dropdownRef} className="hidden md:flex items-center space-x-1">
            {navMenu.map((menu) => (
              <div key={menu.label} className="relative">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === menu.label ? null : menu.label)}
                  onMouseEnter={() => setActiveDropdown(menu.label)}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeDropdown === menu.label
                      ? 'bg-white/10 text-orange-400'
                      : 'text-white/80 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <i className={`${menu.icon} text-xs`}></i>
                  <span>{menu.label}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${activeDropdown === menu.label ? 'rotate-180' : ''}`}></i>
                </button>

                {activeDropdown === menu.label && (
                  <div
                    className="absolute top-full left-0 mt-1 w-48 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {menu.items.map((item) => (
                      <button
                        key={item.sectionId}
                        onClick={() => scrollToSection(item.sectionId)}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all cursor-pointer text-left border-b border-white/5 last:border-0"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-orange-500/20 rounded-md">
                          <i className={`${item.icon} text-orange-400 text-xs`}></i>
                        </div>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={onWhatsAppClick}
              className="ml-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-1.5"
            >
              <i className="ri-whatsapp-line text-sm"></i>
              <span>Contratar</span>
            </button>
          </nav>

          {/* Botão Hamburguer Mobile */}
          <div className="md:hidden flex items-center space-x-2">
            {onPlayRadio && (
              <button
                onClick={onPlayRadio}
                className="flex items-center space-x-1 text-white text-xs bg-white/10 px-3 py-1.5 rounded-full border border-white/10"
              >
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-sm`}></i>
              </button>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white w-8 h-8 flex items-center justify-center"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Menu Mobile */}
      {isMenuOpen && (
        <div className="md:hidden bg-gray-950 border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <div className="py-2">
            {navMenu.map((menu) => (
              <div key={menu.label} className="border-b border-white/5 last:border-0">
                <button
                  onClick={() => setActiveMobileMenu(activeMobileMenu === menu.label ? null : menu.label)}
                  className="w-full flex items-center justify-between px-5 py-3 text-white/90 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-white/10 rounded-lg">
                      <i className={`${menu.icon} text-orange-400 text-sm`}></i>
                    </div>
                    <span className="text-sm font-semibold">{menu.label}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-white/50 transition-transform ${activeMobileMenu === menu.label ? 'rotate-180' : ''}`}></i>
                </button>

                {activeMobileMenu === menu.label && (
                  <div className="bg-black/30">
                    {menu.items.map((item) => (
                      <button
                        key={item.sectionId}
                        onClick={() => scrollToSection(item.sectionId)}
                        className="w-full flex items-center space-x-3 px-8 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all cursor-pointer text-left"
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${item.icon} text-orange-400 text-xs`}></i>
                        </div>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="px-5 py-3">
              <button
                onClick={() => { onWhatsAppClick(); setIsMenuOpen(false); }}
                className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center space-x-2"
              >
                <i className="ri-whatsapp-line"></i>
                <span>Contratar DJ Jean</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
